import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import type { Episode, EpisodeSetupResponse, MediaItem } from '../types';
import PickStep from '../components/create/PickStep';
import ReviewStep from '../components/create/ReviewStep';
import QuestionsStep from '../components/create/QuestionsStep';
import StyleStep from '../components/create/StyleStep';
import PeopleStep from '../components/create/PeopleStep';
import { primaryBtn, secondaryBtn } from '../components/create/ui';

type Step = 'review' | 'people' | 'questions' | 'style' | 'done';
const STEPS: Step[] = ['review', 'people', 'questions', 'style', 'done'];

/**
 * "Make an episode", a guided flow that works one-handed on a phone:
 *   /create             1 pick memories -> 2 processing
 *   /create/:episodeId  3 your episode -> 4 people -> 5 questions -> 6 music & look -> done
 * The step lives in the URL (?step=), so a refresh resumes where you were, and
 * steps 3–6 also work on any existing episode.
 */
export default function CreateEpisodePage() {
  const { episodeId } = useParams();
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-3xl px-4 pt-8 pb-16 sm:px-6">
      {episodeId ? (
        <SetupFlow key={episodeId} episodeId={episodeId} />
      ) : (
        <PickStep onCreated={(id) => navigate(`/create/${id}?step=review`, { replace: true })} />
      )}
    </div>
  );
}

function SetupFlow({ episodeId }: { episodeId: string }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('step') as Step | null;
  const step: Step = raw && STEPS.includes(raw) ? raw : 'review';
  const go = useCallback((s: Step) => setParams({ step: s }, { replace: false }), [setParams]);

  const [episode, setEpisode] = useState<Episode | null>(null);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [data, setData] = useState<EpisodeSetupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [ep, media, setup] = await Promise.all([
        api.episode(episodeId),
        api.episodeMedia(episodeId),
        api.episodeSetup(episodeId),
      ]);
      setEpisode(ep);
      setItems(media.items);
      setData(setup);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [episodeId]);
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  if (error) {
    return (
      <div className="py-24 text-center">
        <h1 className="mb-2 text-2xl font-bold">Episode unavailable</h1>
        <p className="text-white/55">{error}</p>
      </div>
    );
  }
  if (!episode || !data) {
    return (
      <div aria-busy="true">
        <div className="skeleton mb-4 h-8 w-64" />
        <div className="skeleton h-40 w-full" />
      </div>
    );
  }

  if (step === 'review') {
    return (
      <ReviewStep
        episode={episode}
        items={items}
        detected={data.detected}
        onDone={async () => {
          await load();
          go('people');
        }}
      />
    );
  }
  if (step === 'people') {
    return <PeopleStep episodeId={episode.id} onBack={() => go('review')} onDone={() => go('questions')} />;
  }
  if (step === 'questions') {
    return (
      <QuestionsStep
        episodeId={episode.id}
        setup={data.setup}
        onBack={() => go('people')}
        onDone={(setup, suggested) => {
          setData({ ...data, setup, suggested });
          go('style');
        }}
      />
    );
  }
  if (step === 'style') {
    return (
      <StyleStep
        key={`${data.suggested.musicMood}:${data.suggested.look}`}
        episodeId={episode.id}
        setup={data.setup}
        suggested={data.suggested}
        looks={data.looks}
        coverUrl={episode.coverUrl}
        onBack={() => go('questions')}
        onDone={async () => {
          await load();
          go('done');
        }}
      />
    );
  }

  return (
    <div className="text-center">
      <p className="text-xs font-semibold tracking-wider text-emerald-300/80 uppercase">Episode ready</p>
      <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-5xl">{episode.title}</h1>
      {episode.coverUrl && (
        <img src={episode.coverUrl} alt="" className="mx-auto mt-6 aspect-video w-full max-w-xl rounded-2xl object-cover shadow-card" />
      )}
      <p className="mx-auto mt-5 max-w-md text-sm text-white/55">
        It’s in your {data.detected.season ? `${data.detected.season.title}` : 'Undated memories'} with your music and look.
        Automatic grouping will never move these memories out of it.
      </p>
      <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
        <Link to={`/play/${episode.id}`} className={`${primaryBtn} px-8 text-center`}>
          ▶ Play episode
        </Link>
        <Link to={`/episode/${episode.id}#story`} className={`${secondaryBtn} text-center`}>
          ✨ Make it a story
        </Link>
      </div>
      <p className="mx-auto mt-3 max-w-md text-xs text-white/40">
        Optional: the AI writes the opening, a line for each moment and the ending, in English and Urdu.
      </p>
      <Link to={`/episode/${episode.id}`} className="focus-ring mt-4 inline-block rounded text-xs text-white/40 hover:text-white/75">
        Open episode page
      </Link>
      <br />
      <button onClick={() => go('people')} className="focus-ring mt-3 rounded text-xs text-white/40 hover:text-white/75">
        Change the people, answers, music or look
      </button>
    </div>
  );
}
